import React, { useEffect, useState, useCallback } from 'react';
import '../super-admin.css';
import { 
  Tag, Typography, Card, Row, Col, Input, Select, Button, 
  Statistic, Collapse, List, Avatar, message, Modal, Form 
} from 'antd';
import { 
  GlobalOutlined, UserOutlined, SearchOutlined, ReloadOutlined,
  ShopOutlined, MailOutlined, PhoneOutlined, EnvironmentOutlined,
  PlusOutlined 
} from '@ant-design/icons';
import { API_BASE as apiBase, authHeaders } from '../../config/api';

const { Title } = Typography;
const { Panel } = Collapse;

const getStatusColor = (status) => {
  const colors = {
    'active': 'green',
    'inactive': 'red',
    'pending': 'orange',
    'suspended': 'purple'
  };
  return colors[status] || 'default';
};

const AllWebsites = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total_websites: 0,
    active_websites: 0,
    inactive_websites: 0,
    pending_websites: 0,
    total_clients_with_websites: 0,
    new_websites_this_month: 0
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Add website modal states
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [form] = Form.useForm();
  const selectedIndustry = Form.useWatch?.('industry', form);

  // Load clients and their websites
  const loadClientsWebsites = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: '50',
        offset: '0'
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      
      const res = await fetch(`${apiBase}/admin/clients-websites?${params}`, {
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to load clients and websites');
      const data = await res.json();
      
      setClients(data.clients || []);
      
    } catch (err) {
      message.error(err.message || 'Error loading clients and websites');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter]);

  // Load website statistics
  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/admin/website-stats`, {
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to load website statistics');
      const data = await res.json();
      
      setStats(data);
    } catch (err) {
      console.error('Error loading website statistics:', err);
    }
  }, []);

  // Load all users for the dropdown
  const loadAllUsers = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/admin/users`, {
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to load users');
      const data = await res.json();
      // Backend returns an array of users: [{ id, name, email, role }]
      setAllUsers(Array.isArray(data) ? data : (data.users || []));
    } catch (err) {
      console.error('Error loading users:', err);
      message.error('Failed to load users');
    }
  }, []);

  useEffect(() => {
    loadClientsWebsites();
    loadStats();
  }, [loadClientsWebsites, loadStats]);

  const handleRefresh = () => {
    loadClientsWebsites();
    loadStats();
  };

  // Handle opening add website modal
  const handleAddWebsite = () => {
    setAddModalVisible(true);
    loadAllUsers();
  };

  // Handle adding website to user
  const handleAddWebsiteSubmit = async (values) => {
    setAddLoading(true);
    try {
      // Normalize URL to ensure proper format
      let normalizedUrl = values.url.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = 'https://' + normalizedUrl;
      }
      
      const res = await fetch(`${apiBase}/admin/add-website-to-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify({
          user_id: values.user_id,
          url: normalizedUrl,
          industry: values.industry === 'Other'
            ? (values.customIndustry || 'Other')
            : values.industry,
          status: values.status || 'active'
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to add website');
      }

      message.success('Website added successfully');
      setAddModalVisible(false);
      form.resetFields();
      handleRefresh();
    } catch (err) {
      message.error(err.message || 'Failed to add website');
    } finally {
      setAddLoading(false);
    }
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setAddModalVisible(false);
    form.resetFields();
  };

  return (
    <div className="sa-content">
      <Title level={2} style={{ marginBottom: 24 }}>Clients & Websites Management</Title>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} lg={4}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Total Websites"
              value={stats.total_websites}
              prefix={<GlobalOutlined style={{ color: '#3730a3' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Active"
              value={stats.active_websites}
              valueStyle={{ color: '#059669' }}
              prefix={<GlobalOutlined style={{ color: '#059669' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Inactive"
              value={stats.inactive_websites}
              valueStyle={{ color: '#dc2626' }}
              prefix={<GlobalOutlined style={{ color: '#dc2626' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Pending"
              value={stats.pending_websites}
              valueStyle={{ color: '#d97706' }}
              prefix={<GlobalOutlined style={{ color: '#d97706' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Clients"
              value={stats.total_clients_with_websites}
              prefix={<UserOutlined style={{ color: '#7c3aed' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="New This Month"
              value={stats.new_websites_this_month}
              valueStyle={{ color: '#0891b2' }}
              prefix={<GlobalOutlined style={{ color: '#0891b2' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content Card */}
      <Card 
        hoverable 
        style={{ borderRadius: 14 }} 
        bodyStyle={{ padding: 20 }}
        loading={loading}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ 
              width: 44, 
              height: 44, 
              borderRadius: 10, 
              background: '#3730a322', 
              color: '#3730a3', 
              display: 'grid', 
              placeItems: 'center', 
              fontSize: 20 
            }}>
              <UserOutlined />
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937' }}>Clients & Their Websites</div>
              <div style={{ color: '#64748b', fontSize: 13 }}>Manage client accounts and website portfolios</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAddWebsite}
            >
              Add Website
            </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ 
          display: 'flex', 
          gap: 12, 
          flexWrap: 'wrap',
          padding: '12px 16px', 
          background: '#f8fafc', 
          borderRadius: 8, 
          marginBottom: 16 
        }}>
          <Input
            placeholder="Search clients, companies, or emails..."
            prefix={<SearchOutlined />}
            style={{ minWidth: 300, flex: 1 }}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onPressEnter={loadClientsWebsites}
          />
          <Select
            placeholder="Filter by website status"
            style={{ minWidth: 150 }}
            value={statusFilter}
            onChange={setStatusFilter}
            allowClear
          >
            <Select.Option value="active">Active</Select.Option>
            <Select.Option value="inactive">Inactive</Select.Option>
            <Select.Option value="pending">Pending</Select.Option>
            <Select.Option value="suspended">Suspended</Select.Option>
          </Select>
          <Button type="primary" icon={<SearchOutlined />} onClick={loadClientsWebsites}>
            Search
          </Button>
        </div>

        {/* Clients List */}
        <div style={{ 
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          overflow: 'hidden'
        }}>
          {clients.length === 0 && !loading ? (
            <div style={{ 
              padding: 32, 
              textAlign: 'center',
              color: '#64748b' 
            }}>
              <UserOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
              <div>No clients found</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>
                Try adjusting your search filters
              </div>
            </div>
          ) : (
            <Collapse 
              ghost 
              expandIconPosition="end"
              style={{ background: 'white' }}
            >
              {clients.map((client, index) => (
                <Panel
                  key={client.user_id}
                  header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
                      <Avatar 
                        size={40}
                        style={{ backgroundColor: '#3730a3' }}
                        icon={<UserOutlined />}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
                            {client.client_name || 'Unknown Client'}
                          </span>
                          {client.business_account?.company_name && (
                            <Tag color="blue" style={{ margin: 0 }}>
                              {client.business_account.company_name}
                            </Tag>
                          )}
                          <Tag color="purple" style={{ margin: 0 }}>
                            {client.website_count} website{client.website_count !== 1 ? 's' : ''}
                          </Tag>
                        </div>
                        <div style={{ color: '#64748b', fontSize: 13 }}>
                          {client.client_email}
                          {client.business_account?.industry && (
                            <span> • {client.business_account.industry}</span>
                          )}
                          {client.business_account?.subscription_plan && (
                            <span> • {client.business_account.subscription_plan} Plan</span>
                          )}
                        </div>
                      </div>
                    </div>
                  }
                  style={{ 
                    borderBottom: index < clients.length - 1 ? '1px solid #f1f5f9' : 'none',
                    margin: 0
                  }}
                >
                  <div style={{ paddingLeft: 52 }}>
                    {/* Business Account Info */}
                    {client.business_account && (
                      <div style={{ 
                        background: '#f8fafc', 
                        padding: 16, 
                        borderRadius: 8, 
                        marginBottom: 16 
                      }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>
                          <ShopOutlined style={{ marginRight: 8 }} />
                          Business Information
                        </div>
                        <Row gutter={[16, 8]}>
                          <Col xs={24} sm={12}>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>Company</div>
                            <div style={{ fontSize: 14, color: '#1f2937' }}>
                              {client.business_account.company_name || 'N/A'}
                            </div>
                          </Col>
                          <Col xs={24} sm={12}>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>Industry</div>
                            <div style={{ fontSize: 14, color: '#1f2937' }}>
                              {client.business_account.industry || 'N/A'}
                            </div>
                          </Col>
                          {client.business_account.business_email && (
                            <Col xs={24} sm={12}>
                              <div style={{ fontSize: 12, color: '#6b7280' }}>Business Email</div>
                              <div style={{ fontSize: 14, color: '#1f2937' }}>
                                <MailOutlined style={{ marginRight: 4 }} />
                                {client.business_account.business_email}
                              </div>
                            </Col>
                          )}
                          {client.business_account.contact && (
                            <Col xs={24} sm={12}>
                              <div style={{ fontSize: 12, color: '#6b7280' }}>Contact</div>
                              <div style={{ fontSize: 14, color: '#1f2937' }}>
                                <PhoneOutlined style={{ marginRight: 4 }} />
                                {client.business_account.contact}
                              </div>
                            </Col>
                          )}
                          {client.business_account.address && (
                            <Col xs={24}>
                              <div style={{ fontSize: 12, color: '#6b7280' }}>Address</div>
                              <div style={{ fontSize: 14, color: '#1f2937' }}>
                                <EnvironmentOutlined style={{ marginRight: 4 }} />
                                {client.business_account.address}
                              </div>
                            </Col>
                          )}
                        </Row>
                      </div>
                    )}

                    {/* Websites List */}
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>
                        <GlobalOutlined style={{ marginRight: 8 }} />
                        Websites ({client.website_count})
                      </div>
                      {client.websites.length === 0 ? (
                        <div style={{ 
                          padding: 16, 
                          textAlign: 'center', 
                          color: '#64748b',
                          background: '#f9fafb',
                          borderRadius: 8
                        }}>
                          <GlobalOutlined style={{ fontSize: 24, marginBottom: 8, opacity: 0.3 }} />
                          <div>No websites found for this client</div>
                        </div>
                      ) : (
                        <List
                          size="small"
                          dataSource={client.websites}
                          renderItem={(website, index) => (
                            <List.Item 
                              key={`${client.user_id}-${website.id}-${index}`}
                              style={{ 
                                padding: '8px 12px',
                                background: website.source === 'business_account' ? '#f0f9ff' : '#ffffff',
                                border: `1px solid ${website.source === 'business_account' ? '#bfdbfe' : '#e5e7eb'}`,
                                borderRadius: 6,
                                marginBottom: 8
                              }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                                <GlobalOutlined style={{ 
                                  color: website.source === 'business_account' ? '#2563eb' : '#3730a3' 
                                }} />
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                                    <span style={{ fontSize: 14, fontWeight: 500, color: '#1f2937' }}>
                                      {website.url}
                                    </span>
                                    {website.source === 'business_account' && (
                                      <Tag size="small" color="blue" style={{ fontSize: 10 }}>
                                        Business Account
                                      </Tag>
                                    )}
                                    {website.source === 'websites_table' && (
                                      <Tag size="small" color="purple" style={{ fontSize: 10 }}>
                                        Managed Website
                                      </Tag>
                                    )}
                                  </div>
                                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                                    Created: {new Date(website.created_at).toLocaleDateString()}
                                  </div>
                                </div>
                                <Tag color={getStatusColor(website.status)}>
                                  {website.status}
                                </Tag>
                              </div>
                            </List.Item>
                          )}
                        />
                      )}
                    </div>
                  </div>
                </Panel>
              ))}
            </Collapse>
          )}
        </div>
      </Card>

      {/* Add Website Modal */}
      <Modal
        title="Add Website to User"
        open={addModalVisible}
        onCancel={handleModalCancel}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddWebsiteSubmit}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            label="Select User"
            name="user_id"
            rules={[{ required: true, message: 'Please select a user' }]}
          >
            <Select
              placeholder="Search and select a user"
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              options={allUsers.map(user => ({
                value: user.id,
                label: `${user.name || 'Unknown'} (${user.email})`
              }))}
              style={{ 
                width: '100%',
                minHeight: '40px'
              }}
              size="large"
              dropdownStyle={{
                maxHeight: '300px',
                overflow: 'auto'
              }}
              optionLabelProp="label"
              displayRender={(labels) => {
                if (labels && labels.length > 0) {
                  const label = labels[0];
                  // Truncate long text to fit in the select box
                  return label.length > 40 ? `${label.substring(0, 37)}...` : label;
                }
                return '';
              }}
            />
          </Form.Item>

          <Form.Item
            label="Website URL"
            name="url"
            rules={[
              { required: true, message: 'Please enter website URL' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const urlPattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/.*)?$/;
                  const cleanValue = value.trim();
                  
                  // Allow various formats: nytimes.com, www.nytimes.com, https://nytimes.com, etc.
                  if (urlPattern.test(cleanValue)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Please enter a valid website URL (e.g., nytimes.com, www.nytimes.com, or https://nytimes.com)'));
                }
              }
            ]}
          >
            <Input 
              placeholder="nytimes.com, www.nytimes.com, or https://nytimes.com" 
              addonBefore="https://"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            label="Industry"
            name="industry"
            rules={[{ required: true, message: 'Please select an industry' }]}
          >
            <Select placeholder="Select industry">
              <Select.Option value="E-commerce">E-commerce</Select.Option>
              <Select.Option value="Non-profit organization">Non-profit organization</Select.Option>
              <Select.Option value="SaaS product / web app">SaaS product / web app</Select.Option>
              <Select.Option value="Insurance company">Insurance company</Select.Option>
              <Select.Option value="Digital marketing agency">Digital marketing agency</Select.Option>
              <Select.Option value="SME or small business">SME or small business</Select.Option>
              <Select.Option value="University or educational institution">University or educational institution</Select.Option>
              <Select.Option value="Other">Other (please specify)</Select.Option>
            </Select>
          </Form.Item>
          {selectedIndustry === 'Other' && (
            <Form.Item
              label="Describe industry"
              name="customIndustry"
              rules={[{ required: true, message: 'Please enter the industry' }]}
            >
              <Input placeholder="e.g., Real Estate, Hospitality" />
            </Form.Item>
          )}

          <Form.Item
            label="Status"
            name="status"
            initialValue="active"
          >
            <Select>
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="inactive">Inactive</Select.Option>
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="suspended">Suspended</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleModalCancel} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={addLoading}>
              Add Website
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AllWebsites;
